import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { Button } from './Button';
import { IconButton } from './IconButton';
import styles from './Toast.module.css';

export type ToastSeverity = 'success' | 'error' | 'info';
export type ToastPlacement = 'bottom' | 'top';

export interface ToastProps {
  open: boolean;
  message: string;
  severity?: ToastSeverity;
  /** Auto-dismiss after this many ms; pass `null` to require an explicit close. MUI Snackbar-style. */
  autoHideDuration?: number | null;
  /**
   * Where it sits. Defaults to `bottom` — every existing caller is a confirmation of something the
   * reader just did, and the bottom keeps it out of the way. `top` is for a toast the reader did
   * NOT trigger from the screen they are on, which has to be noticed rather than glanced at.
   */
  placement?: ToastPlacement;
  /**
   * Render an explicit ✕ and stop the body being a click target. Pair it with
   * `autoHideDuration={null}` for a toast that must be acknowledged: with a ✕ on screen, a toast
   * that also vanishes on its own teaches the reader the button does not matter.
   */
  dismissible?: boolean;
  /** One step that reverses what the toast confirms ("Undo"). The body stops closing on a click with it on screen. */
  action?: { label: string; onClick: () => void };
  onClose?: () => void;
}

// The dot the DS Sonner draws (Figma 14060:191440) is the primary ink; severity only ever recolours it.
const ACCENT: Record<ToastSeverity, string> = {
  success: 'var(--ds-green-success-600)',
  error: 'var(--ds-fill-destructive-hover)',
  info: 'var(--ds-text-primary)',
};

// A single overlay toast (MUI Snackbar-flavoured: `open`/`message`/`onClose`/`autoHideDuration`).
// Fixed to the viewport bottom so it never shifts page content; fades + slides in on open and back
// out before unmounting. Content is snapshotted on open so it stays put through the exit animation
// even if the caller clears `message` the same tick it flips `open` to false.
export function Toast({ open, message, severity = 'info', autoHideDuration = 4000, placement = 'bottom', dismissible = false, action, onClose }: ToastProps) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [content, setContent] = useState({ message, severity });
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) {
      if (exitTimer.current) clearTimeout(exitTimer.current);
      setContent({ message, severity });
      setMounted(true);
      const r = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setVisible(false);
    // Fallback in case the exit transitionend doesn't fire (e.g. closed before it finished entering).
    exitTimer.current = setTimeout(() => setMounted(false), 320);
    return () => {
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || autoHideDuration == null) return;
    const id = setTimeout(() => onClose?.(), autoHideDuration);
    return () => clearTimeout(id);
  }, [open, autoHideDuration, onClose]);

  if (!mounted) return null;

  return (
    <div className={`${styles.wrap} ${placement === 'top' ? styles.top : ''}`}>
      <div
        role={content.severity === 'error' ? 'alert' : 'status'}
        aria-live={content.severity === 'error' ? 'assertive' : 'polite'}
        // Click-anywhere-to-close only when there is no ✕: with a button on screen, a body that
        // also closes makes a misclick look like the button doing something else.
        onClick={dismissible || action ? undefined : onClose}
        onTransitionEnd={() => {
          if (!open) setMounted(false);
        }}
        className={`${styles.toast} ${visible ? styles.visible : ''} ${dismissible ? styles.withClose : ''}`}
      >
        <span aria-hidden className={styles.dot} style={{ background: ACCENT[content.severity] }} />
        <span>{content.message}</span>
        {action ? (
          <Button variant="text" size="small" onClick={action.onClick} className={styles.action}>
            {action.label}
          </Button>
        ) : null}
        {dismissible ? (
          // The DS icon button, not a hand-rolled ✕ — `tiny` is the smallest the component
          // offers and keeps the pill at its designed 44 height.
          <IconButton size="tiny" variant="text" onClick={onClose} aria-label={t('common.close')} className={styles.close}>
            <X size={16} aria-hidden />
          </IconButton>
        ) : null}
      </div>
    </div>
  );
}
